/**
 * Whether the system itself is available to the people using it.
 *
 * Desk, 2026-09-27: "when the backend or frontend is being deployed or updated,
 * users should be told the page is working, to come back after 5 minutes… what
 * happens is users lose actions while the backend is busy and think the
 * application is bad."
 *
 *   ok         — nothing to say.
 *   updating   — the server has stopped answering (a deploy or restart). Users are
 *                told to wait and to CHECK before repeating an action.
 *   recovered  — it answers again. Users are told it is back and to check their
 *                last action; they acknowledge it and carry on.
 *   reload     — a new version of the screens was installed while this one was
 *                open, so the next page it needs no longer exists.
 */
import { create } from 'zustand';

export type SystemPhase = 'ok' | 'updating' | 'recovered' | 'reload';

interface SystemStatusState {
  phase: SystemPhase;
  /** ISO time the outage was first confirmed. */
  since: string | null;
  /** ISO time of the most recent automatic check. */
  lastCheckedAt: string | null;
  markUpdating: () => void;
  markRecovered: () => void;
  markReload: () => void;
  markChecked: () => void;
  /** The user has read the notice. */
  dismiss: () => void;
}

export const useSystemStatusStore = create<SystemStatusState>()((set, get) => ({
  phase: 'ok',
  since: null,
  lastCheckedAt: null,
  markUpdating: () => {
    if (get().phase === 'updating') return;
    set({ phase: 'updating', since: new Date().toISOString(), lastCheckedAt: null });
  },
  markRecovered: () => set({ phase: 'recovered' }),
  markReload: () => {
    // An outage notice outranks it: the reload advice is meaningless while nothing answers.
    if (get().phase === 'ok') set({ phase: 'reload' });
  },
  markChecked: () => set({ lastCheckedAt: new Date().toISOString() }),
  dismiss: () => set({ phase: 'ok', since: null, lastCheckedAt: null }),
}));
